// Location detail + editor — slug, type, country, parent, category, map,
// status. Image / banner / hero uploads come in v2 (need new presign +
// finalize endpoints; deferred). Localised name + content stay in CMS.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { MediaUploader } from '@drivecars/ui';
import { DefRow, DetailLayout, DetailSection } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import {
  TextField,
  TextAreaField,
  NumberField,
  SelectField,
  RelationshipPicker,
  type RelationshipOption,
  type SelectOption,
} from '@/components/forms';
import { env } from '@/lib/env';
import {
  deleteLocationBanner,
  deleteLocationImage,
  fetchAdminLocationCategories,
  fetchAdminLocationDetail,
  fetchAdminLocations,
  patchLocation,
  uploadLocationBanner,
  uploadLocationImage,
  type AdminLocationCountryCode,
  type AdminLocationDetail,
  type AdminLocationStatus,
  type AdminLocationType,
  type LocationPatchInput,
} from '@/lib/admin-api';

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

const TYPE_OPTIONS: SelectOption<AdminLocationType>[] = [
  { value: 'country', label: 'Country' },
  { value: 'region', label: 'Region' },
  { value: 'city', label: 'City' },
  { value: 'airport', label: 'Airport' },
  { value: 'station', label: 'Train station' },
];
const STATUS_OPTIONS: SelectOption<AdminLocationStatus>[] = [
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
];
const COUNTRY_OPTIONS: SelectOption<AdminLocationCountryCode | ''>[] = [
  { value: '', label: '— (no country)' },
  { value: 'ma', label: 'Morocco (MA)' },
  { value: 'ae', label: 'UAE (AE)' },
  { value: 'es', label: 'Spain (ES)' },
  { value: 'gb', label: 'United Kingdom (GB)' },
  { value: 'us', label: 'United States (US)' },
];

interface LocationDraft {
  slug: string;
  type: AdminLocationType;
  countryCode: AdminLocationCountryCode | '';
  status: AdminLocationStatus;
  topRank: number | null;
  parentId: number | null;
  parentOption: RelationshipOption | null;
  categoryId: number | null;
  categoryOption: RelationshipOption | null;
  mapLatitude: number | null;
  mapLongitude: number | null;
  mapZoom: number | null;
  // Per-locale name + description (locations_locales), editable in-app.
  locales: Record<LocLocale, LocLocaleFields>;
}

type LocLocale = 'en' | 'fr' | 'ar' | 'es';
const LOC_LOCALES: LocLocale[] = ['en', 'fr', 'ar', 'es'];
interface LocLocaleFields {
  name: string;
  content: string;
}
function locLocaleFieldsFrom(l: AdminLocationDetail, lang: LocLocale): LocLocaleFields {
  return {
    name: l.localised.name[lang] ?? '',
    content: l.localised.content[lang] ?? '',
  };
}

function locationToDraft(l: AdminLocationDetail): LocationDraft {
  return {
    slug: l.slug,
    type: l.type,
    countryCode: l.countryCode ?? '',
    status: l.status,
    topRank: l.topRank,
    parentId: l.parentId,
    parentOption: l.parent
      ? {
          id: l.parent.id,
          label: l.parent.name ?? l.parent.slug,
          sub: l.parent.slug,
        }
      : null,
    categoryId: l.categoryId,
    categoryOption: l.category
      ? {
          id: l.category.id,
          label: l.category.name ?? `Category #${l.category.id}`,
          sub: l.category.iconClass,
        }
      : null,
    mapLatitude: l.map.latitude,
    mapLongitude: l.map.longitude,
    mapZoom: l.map.zoom,
    locales: Object.fromEntries(
      LOC_LOCALES.map((lang) => [lang, locLocaleFieldsFrom(l, lang)]),
    ) as Record<LocLocale, LocLocaleFields>,
  };
}

function buildPatch(l: AdminLocationDetail, d: LocationDraft): LocationPatchInput {
  const p: LocationPatchInput = {};
  if (d.slug.trim() !== l.slug) p.slug = d.slug.trim();
  if (d.type !== l.type) p.type = d.type;
  const cc = d.countryCode === '' ? null : d.countryCode;
  if (cc !== l.countryCode) p.countryCode = cc;
  if (d.status !== l.status) p.status = d.status;
  if (d.topRank !== l.topRank) p.topRank = d.topRank;
  if (d.parentId !== l.parentId) p.parentId = d.parentId;
  if (d.categoryId !== l.categoryId) p.categoryId = d.categoryId;
  const mapDirty =
    d.mapLatitude !== l.map.latitude ||
    d.mapLongitude !== l.map.longitude ||
    d.mapZoom !== l.map.zoom;
  if (mapDirty)
    p.map = {
      latitude: d.mapLatitude,
      longitude: d.mapLongitude,
      zoom: d.mapZoom,
    };
  const locales: NonNullable<LocationPatchInput['locales']> = {};
  for (const lang of LOC_LOCALES) {
    const orig = locLocaleFieldsFrom(l, lang);
    if (JSON.stringify(d.locales[lang]) !== JSON.stringify(orig)) {
      locales[lang] = { name: d.locales[lang].name.trim(), content: d.locales[lang].content };
    }
  }
  if (Object.keys(locales).length > 0) p.locales = locales;
  return p;
}

function resolveMediaUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

// ─── Page ────────────────────────────────────────────────────────────

export function LocationDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'locations', id],
    queryFn: ({ signal }) => fetchAdminLocationDetail(id, signal),
  });

  const [draft, setDraft] = useState<LocationDraft | null>(null);
  useEffect(() => {
    if (data) setDraft(locationToDraft(data));
  }, [data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });
  };

  const saveMut = useMutation({
    mutationFn: (body: LocationPatchInput) => patchLocation(id, body),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'locations', id] });
    },
  });

  if (isLoading || !draft) {
    return (
      <DetailLayout title="Loading…" backTo="/locations" backLabel="All locations">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading location…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/locations" backLabel="All locations">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No location with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const l = data;
  const display = l.localised.name.en?.trim() || l.slug;

  const patch = buildPatch(l, draft);
  const dirty = Object.keys(patch).length > 0;

  const setField = <K extends keyof LocationDraft>(k: K, val: LocationDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: val } : d));
  const setLocaleField = (lang: LocLocale, k: keyof LocLocaleFields, v: string) =>
    setDraft((d) =>
      d ? { ...d, locales: { ...d.locales, [lang]: { ...d.locales[lang], [k]: v } } } : d,
    );

  return (
    <DetailLayout
      eyebrow="Operate · Location"
      title={display}
      sub={`${l.type}${l.countryCode ? ` · ${l.countryCode.toUpperCase()}` : ''} · ${l.slug}`}
      backTo="/locations"
      backLabel="All locations"
      actions={<StatusPill status={l.status} />}
    >
      <LocationTranslationsEditor draft={draft} onChange={setLocaleField} />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(locationToDraft(l))}
        onSave={() => saveMut.mutate(patch)}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DetailSection title="Identity">
          <div className="space-y-4">
            <TextField
              label="Slug"
              monospace
              required
              value={draft.slug}
              onChange={(v) => setField('slug', v)}
              hint="URL-safe identifier used in /<country>/<lang>/<location-slug> URLs. Kebab-case only."
            />
            <SelectField
              label="Type"
              required
              value={draft.type}
              onChange={(v) => setField('type', v)}
              options={TYPE_OPTIONS}
              hint="Drives the home-page tabs (cities vs airports/stations) and the listing URL."
            />
            <SelectField
              label="Country code"
              value={draft.countryCode}
              onChange={(v) => setField('countryCode', v)}
              options={COUNTRY_OPTIONS}
              hint="Two-letter URL country segment. Set on cities/airports/stations so the home-page tabs can filter by country."
            />
            <SelectField
              label="Status"
              required
              value={draft.status}
              onChange={(v) => setField('status', v)}
              options={STATUS_OPTIONS}
              hint="Storefront only renders `published`."
            />
            <NumberField
              label="Top-destinations rank"
              value={draft.topRank}
              onChange={(v) => setField('topRank', v)}
              min={1}
              max={1000}
              step={1}
              hint="1 = most important. Drives the storefront Top Destinations grid, /car-rental ordering and search boost. Unique — saving a taken rank fails; clear to unrank."
            />
          </div>
        </DetailSection>

        <DetailSection title="Hierarchy">
          <div className="space-y-4">
            <RelationshipPicker
              label="Parent"
              value={draft.parentId}
              valueLabel={draft.parentOption?.label}
              valueSub={draft.parentOption?.sub ?? null}
              onChange={(pid, opt) => {
                setField('parentId', pid);
                setField('parentOption', opt ?? null);
              }}
              searchFn={async (q, signal) => {
                const res = await fetchAdminLocations({ q, limit: 20 }, signal);
                return res.rows
                  .filter((r) => r.id !== id) // can't be own parent
                  .map((r) => ({
                    id: r.id,
                    label: r.name ?? r.slug,
                    sub: `${r.type}${r.countryCode ? ` · ${r.countryCode}` : ''}`,
                  }));
              }}
              hint="Optional. e.g. a city's parent is its country."
            />
            <RelationshipPicker
              label="Category"
              value={draft.categoryId}
              valueLabel={draft.categoryOption?.label}
              onChange={(cid, opt) => {
                setField('categoryId', cid);
                setField('categoryOption', opt ?? null);
              }}
              searchFn={async (q, signal) => {
                const res = await fetchAdminLocationCategories({ q, limit: 50 }, signal);
                return res.rows.map((r) => ({
                  id: r.id,
                  label: r.name ?? `Category #${r.id}`,
                  sub: r.iconClass,
                }));
              }}
              hint="Optional taxonomy (Education / Health / Transportation). Used by category-filtered grids."
            />
          </div>
        </DetailSection>

        <DetailSection title="Map">
          <div className="grid grid-cols-3 gap-3">
            <NumberField
              label="Latitude"
              value={draft.mapLatitude}
              onChange={(v) => setField('mapLatitude', v)}
              min={-90}
              max={90}
              step={0.000001}
            />
            <NumberField
              label="Longitude"
              value={draft.mapLongitude}
              onChange={(v) => setField('mapLongitude', v)}
              min={-180}
              max={180}
              step={0.000001}
            />
            <NumberField
              label="Zoom"
              value={draft.mapZoom}
              onChange={(v) => setField('mapZoom', v)}
              min={0}
              max={22}
              step={1}
              hint="0–22"
            />
          </div>
        </DetailSection>

      </div>

      <PhotosSection location={l} />

      <DetailSection title="Timestamps (read-only)">
        <DefRow label="Created">{new Date(l.createdAt).toLocaleString()}</DefRow>
        <DefRow label="Last updated">{new Date(l.updatedAt).toLocaleString()}</DefRow>
      </DetailSection>

      <EntityActivity entityType="locations" entityId={l.id} title="Audit trail" />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(locationToDraft(l))}
        onSave={() => saveMut.mutate(patch)}
      />
    </DetailLayout>
  );
}

// ─── Translations editor (locations_locales: name / description) ────────

function LocationTranslationsEditor({
  draft,
  onChange,
}: {
  draft: LocationDraft;
  onChange: (lang: LocLocale, k: keyof LocLocaleFields, v: string) => void;
}) {
  const [lang, setLang] = useState<LocLocale>('en');
  const f = draft.locales[lang];
  return (
    <DetailSection title="Translations & description">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {LOC_LOCALES.map((l) => {
          const filled = draft.locales[l].name.trim().length > 0;
          return (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={`inline-flex h-8 items-center gap-1 rounded-md border px-3 text-[12px] font-medium ${
                l === lang
                  ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                  : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
              }`}
            >
              {l.toUpperCase()}
              {!filled ? <span className="text-[#d97706]">•</span> : null}
            </button>
          );
        })}
      </div>
      <div className="space-y-4">
        <TextField label="Name" value={f.name} onChange={(v) => onChange(lang, 'name', v)} />
        <TextAreaField
          label="Description"
          value={f.content}
          onChange={(v) => onChange(lang, 'content', v)}
        />
      </div>
    </DetailSection>
  );
}

// ─── Save bar ──────────────────────────────────────────────────────

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Photos section — hero card + page banner ──────────────────────

function PhotosSection({ location }: { location: AdminLocationDetail }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'locations', location.id] });

  const imageUploadMut = useMutation({
    mutationFn: (file: File) => uploadLocationImage(location.id, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });
  const imageDeleteMut = useMutation({
    mutationFn: () => deleteLocationImage(location.id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });
  const bannerUploadMut = useMutation({
    mutationFn: (file: File) => uploadLocationBanner(location.id, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });
  const bannerDeleteMut = useMutation({
    mutationFn: () => deleteLocationBanner(location.id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  const imageUrl = resolveMediaUrl(location.image?.url);
  const bannerUrl = resolveMediaUrl(location.banner?.url);
  const imageBusy = imageUploadMut.isPending || imageDeleteMut.isPending;
  const bannerBusy = bannerUploadMut.isPending || bannerDeleteMut.isPending;

  return (
    <DetailSection title="Photos">
      {error ? (
        <div className="mb-3 rounded-md border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          {error}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
              Card thumbnail
            </span>
            {imageUrl ? (
              <button
                type="button"
                onClick={() => imageDeleteMut.mutate()}
                disabled={imageBusy}
                className="text-[11.5px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            ) : null}
          </div>
          <MediaUploader
            value={imageUrl ? { url: imageUrl } : null}
            uploading={imageBusy}
            maxSize={MAX_UPLOAD_BYTES}
            onChange={(file) => {
              if (file) imageUploadMut.mutate(file);
            }}
            onError={(msg) => setError(msg)}
          />
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
              Hero banner
            </span>
            {bannerUrl ? (
              <button
                type="button"
                onClick={() => bannerDeleteMut.mutate()}
                disabled={bannerBusy}
                className="text-[11.5px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            ) : null}
          </div>
          <MediaUploader
            value={bannerUrl ? { url: bannerUrl } : null}
            uploading={bannerBusy}
            maxSize={MAX_UPLOAD_BYTES}
            onChange={(file) => {
              if (file) bannerUploadMut.mutate(file);
            }}
            onError={(msg) => setError(msg)}
          />
        </div>
      </div>
    </DetailSection>
  );
}
